import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

import styles from './styles';
import useSound from './hooks/useSound';
import usePersistentState from './hooks/usePersistentState';
import useTimer from './hooks/useTimer';
import useAnimation from './hooks/useAnimation';
import useScoringSystem from './hooks/useScoringSystem';
import { EXPECTED_SCAN_TIME } from './hooks/utils';

const ScoringGame = ({
  scannedCount,
  onScoreUpdate = () => {},
  soundsEnabled = true
}) => {
  const [lastScannedCount, setLastScannedCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const playSound = useSound(soundsEnabled);

  const currentStateRef = useRef({
    multiplier: 1,
    accumulatorProgress: 0,
    score: 0
  });

  const persistentState = usePersistentState({
    multiplier: 1,
    accumulatorProgress: 0,
    score: 0
  });

  const animation = useAnimation();

  const scoring = useScoringSystem(
    { multiplier: 1, accumulatorProgress: 0, score: 0 },
    onScoreUpdate,
    playSound
  );

  const timer = useTimer(EXPECTED_SCAN_TIME, null);

  useEffect(() => {
    currentStateRef.current = {
      multiplier: scoring.multiplier,
      accumulatorProgress: scoring.accumulatorProgress,
      score: scoring.score
    };
  }, [scoring.multiplier, scoring.accumulatorProgress, scoring.score]);

  const saveCurrentState = useCallback(async () => {
    try {
      const stateToSave = {
        multiplier: scoring.multiplierRef.current,
        accumulatorProgress: scoring.accumulatorProgressRef.current,
        score: scoring.score
      };

      await persistentState.saveState(stateToSave);
      return true;
    } catch (error) {
      console.error('Error saving state:', error);
      return false;
    }
  }, [scoring, persistentState]);

  useEffect(() => {
    if (scannedCount > lastScannedCount && isInitialized) {
      handleSuccessfulScan();
    }

    if (scannedCount !== lastScannedCount) {
      setLastScannedCount(scannedCount);
    }
  }, [scannedCount, lastScannedCount, isInitialized]);

  // Component initialization
  useEffect(() => {
    let isMounted = true;

    const initializeComponent = async () => {
      try {
        const loadedState = await persistentState.initializeState();

        if (!isMounted) return;

        scoring.setMultiplier(loadedState.multiplier);
        scoring.setAccumulatorProgress(loadedState.accumulatorProgress);
        scoring.setScore(loadedState.score);

        scoring.multiplierRef.current = loadedState.multiplier;
        scoring.accumulatorProgressRef.current = loadedState.accumulatorProgress;

        currentStateRef.current = loadedState;

        timer.startTimer();
        setLastScannedCount(scannedCount);

        scoring.animationActiveRef.current = true;
        scoring.startAccumulatorTimer();

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing component:', error);
        if (!isMounted) return;

        const defaultState = { multiplier: 1, accumulatorProgress: 0, score: 0 };

        scoring.setMultiplier(defaultState.multiplier);
        scoring.setAccumulatorProgress(defaultState.accumulatorProgress);
        scoring.setScore(defaultState.score);

        scoring.multiplierRef.current = defaultState.multiplier;
        scoring.accumulatorProgressRef.current = defaultState.accumulatorProgress;

        currentStateRef.current = defaultState;

        timer.startTimer();
        setLastScannedCount(scannedCount);

        scoring.animationActiveRef.current = true;
        scoring.startAccumulatorTimer();

        setIsInitialized(true);
      }
    };

    initializeComponent();

    return () => {
      isMounted = false;

      scoring.animationActiveRef.current = false;
      scoring.stopAccumulatorTimer();

      const finalState = {
        multiplier: scoring.multiplierRef.current || currentStateRef.current.multiplier,
        accumulatorProgress: scoring.accumulatorProgressRef.current || currentStateRef.current.accumulatorProgress,
        score: scoring.score || currentStateRef.current.score
      };
      persistentState.saveState(finalState).catch(error => {
        console.error('Error saving state during cleanup:', error);
      });
    };
  }, []);

  const handleSuccessfulScan = useCallback(() => {
    timer.resetTimer();

    let accumulatorBoost = 0.05;
    const isPositiveTime = timer.elapsedTimeRef.current < EXPECTED_SCAN_TIME;

    if (isPositiveTime) {
      const secondsRemaining = Math.floor(EXPECTED_SCAN_TIME - timer.elapsedTimeRef.current);
      accumulatorBoost += secondsRemaining * 0.015;
    } else {
      accumulatorBoost = 0.02;
    }

    scoring.increaseAccumulator(accumulatorBoost);
    scoring.increaseScore();
    animation.flashMultiplierDisplay();

    setTimeout(() => {
      saveCurrentState();
    }, 100);
  }, [timer, scoring, animation, saveCurrentState]);

  useEffect(() => {
    if (!isInitialized) return;

    const checkMarkerBounce = () => {
      if (timer.elapsedTime > EXPECTED_SCAN_TIME && !animation.isMarkerBouncingRef.current) {
        const markerPosition = Math.min(
          100,
          Math.max(0, (timer.elapsedTime / EXPECTED_SCAN_TIME) * 50)
        );

        if (markerPosition >= 99) {
          const currentTime = Date.now();
          const timeFromLastBounce = currentTime - animation.lastBounceTime;

          if (timeFromLastBounce > 1000 && scoring.accumulatorProgressRef.current > 0.05) {
            animation.setLastBounceTime(currentTime);
            animation.triggerRedSectionFlash();
            scoring.reduceAccumulator();
            animation.startBounceAnimation(EXPECTED_SCAN_TIME, playSound, (newTime) => {
              timer.setElapsedTime(newTime);
              timer.elapsedTimeRef.current = newTime;
            });

            setTimeout(() => {
              saveCurrentState();
            }, 100);
          }
        }
      }
    };

    checkMarkerBounce();
  }, [timer.elapsedTime, isInitialized, saveCurrentState]);

  useEffect(() => {
    if (!isInitialized) return;

    const autoSaveInterval = setInterval(() => {
      saveCurrentState();
    }, 3000);

    return () => {
      clearInterval(autoSaveInterval);
    };
  }, [isInitialized, saveCurrentState]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isInitialized) {
        saveCurrentState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInitialized, saveCurrentState]);


  if (!isInitialized) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  return (
    <>
      <View style={styles.timerSection}>
        <View style={styles.timerContainer}>
          <LinearGradient
            start={{x: 0, y: 0}}
            end={{x: 1, y: 0}}
            colors={['#07BAE5', '#0D53DE', '#000000']}
            locations={[0, 0.77, 1]}
            style={styles.blueGradient}
          />
          <View
            style={[
              styles.timeMarker,
              {
                left: animation.isMarkerBouncing
                  ? `${animation.calculateBouncePosition(animation.bounceProgress || 0) * 50}%`
                  : `${Math.min(
                      100,
                      Math.max(0, (timer.elapsedTime / EXPECTED_SCAN_TIME) * 50)
                    )}%`,
                transition: animation.isMarkerBouncing ? 'none' : 'left 0.1s linear'
              }
            ]}
          />
          <LinearGradient
            start={{x: 0.5, y: 1}}
            end={{x: 0.5, y: 0}}
            colors={['#2B3642', '#7090B1']}
            style={styles.timerDisplay}>
            <Text style={styles.timerText}>{timer.timerDisplay.substring(0, 8)}</Text>
          </LinearGradient>
          <LinearGradient
            start={{x: 0, y: 0}}
            end={{x: 1, y: 0}}
            colors={
              animation.redSectionFlashing
                ? ['#000000', '#FFFAFA', '#FFFAFA', '#000000']
                : ['#000000', '#710500', '#550300', '#000000']
            }
            locations={[0, 0.6, 0.85, 1]}
            style={styles.redGradient}
          />
        </View>
      </View>

      <View style={styles.scoreSection}>
        <View style={styles.scoreBarContainer}>
          <View
            style={[
              styles.multiplierContainer,
              {
                borderColor: animation.multiplierFlash ? '#7DF9FF' : '#FFFFFF',
                ...(animation.multiplierFlash ? styles.multiplierFlashEffect : {})
              }
            ]}>
            <LinearGradient
              start={{x: 0.5, y: 0}}
              end={{x: 0.5, y: 1}}
              colors={['#424242', '#B5C9DD', '#DCEAFC', '#676E74']}
              locations={[0, 0.29, 0.52, 1]}
              style={[
                styles.multiplierGradient,
                {opacity: animation.multiplierFlash ? 0.5 : 0.3}
              ]}
            />

            <Text
              style={[
                styles.multiplierText,
                {
                  color: animation.multiplierFlash ? '#7DF9FF' : 'white',
                  ...(animation.multiplierFlash ? styles.multiplierTextFlashEffect : {})
                }
              ]}>
              {scoring.multiplier}x
            </Text>
          </View>

          <View style={styles.scoreBarMain}>
            <LinearGradient
              start={{x: 0, y: 0}}
              end={{x: 1, y: 0}}
              colors={['#0090B2', '#0D53DE', '#000000']}
              locations={[0, 0.77, 1]}
              style={styles.scoreBarBackground}
            />

            <View
              style={[
                styles.accumulatorBar,
                {width: `${Math.min(scoring.accumulatorProgress * 100, 100)}%`}
              ]}>
              <LinearGradient
                start={{x: 0.5, y: 0}}
                end={{x: 0.5, y: 1}}
                colors={[
                  'rgba(148, 148, 148, 0.34)',
                  'rgba(181, 201, 221, 0.71)',
                  'rgba(251, 253, 255, 0.56)',
                  'rgba(52, 56, 59, 0.33)'
                ]}
                locations={[0, 0.22, 0.34, 1]}
                style={styles.accumulatorGradient}
              />
            </View>
          </View>
        </View>

        <Text style={styles.scoreText}>{scoring.score.toLocaleString()}</Text>
      </View>
    </>
  );
};

export default ScoringGame;
